import {
  useEffect,
  useRef,
  useState,
  type RefObject,
  type MutableRefObject,
} from "react";

type HistoryEdgeOptions = {
  enabled: boolean;
  direction: "older" | "newer";
  requestLock: MutableRefObject<boolean>;
  preferredDirection: MutableRefObject<"older" | "newer">;
  isLoading: boolean;
  boundaryItemId?: string;
  onLoad: () => Promise<void>;
  scrollRef: RefObject<HTMLDivElement>;
  sentinelRef: RefObject<HTMLDivElement>;
};

export function useChatHistoryEdge({
  enabled,
  direction,
  requestLock,
  preferredDirection,
  isLoading,
  boundaryItemId,
  onLoad,
  scrollRef,
  sentinelRef,
}: HistoryEdgeOptions) {
  const attemptedBoundary = useRef<string>();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (attemptedBoundary.current !== boundaryItemId) {
      attemptedBoundary.current = undefined;
      setFailed(false);
    }
  }, [boundaryItemId]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!enabled || isLoading || pending || failed || !root || !sentinel)
      return;
    let active = true;
    const loadIfNear = () => {
      const rootBounds = root.getBoundingClientRect();
      const edgeBounds = sentinel.getBoundingClientRect();
      const near =
        edgeBounds.bottom >=
          rootBounds.top - (direction === "older" ? 320 : 0) &&
        edgeBounds.top <= rootBounds.bottom + (direction === "newer" ? 320 : 0);
      const overlappingEdges = root.scrollHeight - root.clientHeight < 640;
      if (
        !active ||
        requestLock.current ||
        !near ||
        !boundaryItemId ||
        attemptedBoundary.current === boundaryItemId ||
        (overlappingEdges && preferredDirection.current !== direction)
      )
        return;
      requestLock.current = true;
      preferredDirection.current = direction;
      attemptedBoundary.current = boundaryItemId;
      observer.disconnect();
      setPending(true);
      void onLoad()
        .catch(() => {
          setFailed(true);
        })
        .finally(() => {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              requestLock.current = false;
              setPending(false);
            }),
          );
        });
    };
    const chooseDirection = (event: WheelEvent | KeyboardEvent) => {
      const movement =
        "deltaY" in event
          ? event.deltaY
          : ["ArrowDown", "PageDown", "End"].includes(event.key)
            ? 1
            : ["ArrowUp", "PageUp", "Home"].includes(event.key)
              ? -1
              : 0;
      if (!movement) return;
      preferredDirection.current = movement > 0 ? "newer" : "older";
      loadIfNear();
    };
    let lastScrollTop = root.scrollTop;
    const onScroll = () => {
      const movement = root.scrollTop - lastScrollTop;
      lastScrollTop = root.scrollTop;
      if (!requestLock.current && movement) {
        preferredDirection.current = movement > 0 ? "newer" : "older";
        loadIfNear();
      }
    };
    const observer = new IntersectionObserver(loadIfNear, {
      root,
      rootMargin:
        direction === "older" ? "320px 0px 0px 0px" : "0px 0px 320px 0px",
    });
    root.addEventListener("wheel", chooseDirection, { passive: true });
    root.addEventListener("keydown", chooseDirection);
    root.addEventListener("scroll", onScroll, { passive: true });
    observer.observe(sentinel);
    return () => {
      active = false;
      observer.disconnect();
      root.removeEventListener("wheel", chooseDirection);
      root.removeEventListener("keydown", chooseDirection);
      root.removeEventListener("scroll", onScroll);
    };
  }, [
    enabled,
    direction,
    requestLock,
    preferredDirection,
    failed,
    isLoading,
    boundaryItemId,
    onLoad,
    pending,
    scrollRef,
    sentinelRef,
  ]);

  return {
    failed,
    retry: () => {
      attemptedBoundary.current = undefined;
      setFailed(false);
    },
  };
}
